// ─────────────────────────────────────────────────────────────
// renderMarkdown — lightweight inline markdown → React elements
// Handles: **bold**, *italic*, - bullets, 1. numbered lists, \n
// Safe for handoff note content (no HTML injection).
// ─────────────────────────────────────────────────────────────

import type { ReactNode } from 'react';

// Parse inline **bold** and *italic* within a single line
function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  // Combined regex: **bold** or *italic*
  const re = /(\*\*(.+?)\*\*)|(\*(.+?)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(text.slice(last, m.index));
    }
    if (m[1]) {
      // **bold**
      parts.push(<strong key={`${keyPrefix}-b${idx++}`}>{m[2]}</strong>);
    } else if (m[3]) {
      // *italic*
      parts.push(<em key={`${keyPrefix}-i${idx++}`}>{m[4]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function renderMarkdown(content: string): ReactNode {
  if (!content?.trim()) return null;

  const lines = content.split('\n');
  const result: ReactNode[] = [];
  let ulItems: ReactNode[] = [];
  let olItems: ReactNode[] = [];
  let blockKey = 0;

  const flushUl = () => {
    if (ulItems.length === 0) return;
    result.push(
      <ul key={`ul-${blockKey++}`} style={{ margin: '2px 0 4px', paddingLeft: 20 }}>
        {ulItems}
      </ul>,
    );
    ulItems = [];
  };
  const flushOl = () => {
    if (olItems.length === 0) return;
    result.push(
      <ol key={`ol-${blockKey++}`} style={{ margin: '2px 0 4px', paddingLeft: 20 }}>
        {olItems}
      </ol>,
    );
    olItems = [];
  };

  lines.forEach((line, i) => {
    const key = `line-${i}`;
    const ulMatch = line.match(/^(\s*)-\s(.*)/);
    const olMatch = line.match(/^(\s*)\d+\.\s(.*)/);

    if (ulMatch) {
      flushOl();
      ulItems.push(
        <li key={key} style={{ fontSize: 13 }}>
          {parseInline(ulMatch[2], key)}
        </li>,
      );
    } else if (olMatch) {
      flushUl();
      olItems.push(
        <li key={key} style={{ fontSize: 13 }}>
          {parseInline(olMatch[2], key)}
        </li>,
      );
    } else {
      flushUl();
      flushOl();
      if (line.trim() === '') {
        // Blank line — small gap
        result.push(<div key={key} style={{ height: 4 }} />);
      } else {
        result.push(
          <p key={key} style={{ margin: '0 0 2px', fontSize: 13, lineHeight: 1.6 }}>
            {parseInline(line, key)}
          </p>,
        );
      }
    }
  });

  flushUl();
  flushOl();

  return <>{result}</>;
}
